// eslint-disable-next-line no-restricted-imports
import type { unitOfTime } from "moment";
import React from "react";

import { MetricTypes } from "../autogen/metrics";

export type UIElement = React.JSX.Element | null;
export type EventHandler = ((ev: React.SyntheticEvent) => Promise<void>) | ((ev: React.SyntheticEvent) => void);

export type FieldValue = {
  name: string;
  comparator: "=" | "!=" | ">" | "<" | ">=" | "<=" | "IS" | "IS NOT";
  value: string;
};
export type DimensionValue = FieldValue;
export type MetricValue = FieldValue;

export type CustomTimeRange = {
  start;
  end;
};

export type TimeRange =
  | "TO_DATE"
  | "LAST_7_DAYS"
  | "LAST_7_DAYS_LAST_YEAR"
  | "LAST_30_DAYS"
  | "LAST_30_DAYS_LAST_YEAR"
  | "LAST_60_DAYS"
  | "LAST_60_DAYS_LAST_YEAR"
  | "LAST_90_DAYS"
  | "LAST_90_DAYS_LAST_YEAR"
  | "LAST_365_DAYS"
  | "LAST_365_DAYS_LAST_YEAR"
  | "MONTH_TO_DATE"
  | "MONTH_TO_DATE_LAST_YEAR"
  | "QUARTER_TO_DATE"
  | "QUARTER_TO_DATE_LAST_YEAR"
  | "YEAR_TO_DATE"
  | "YEAR_TO_DATE_LAST_YEAR"
  | "CUSTOM";

export type TemporalResolution = {
  resolution: "DAILY" | "WEEKLY" | "MONTHLY" | "QUARTERLY";
  label: string;
  duration: unitOfTime.DurationConstructor;
  startOf: unitOfTime.StartOf;
  xAxisLabelFormat: string;
};

export const TemporalResolutions: { [key: string]: TemporalResolution } = {
  DAILY: { resolution: "DAILY", label: "Daily", duration: "day", startOf: "day", xAxisLabelFormat: "D MMM" },
  WEEKLY: {
    resolution: "WEEKLY",
    label: "Weekly",
    duration: "week",
    startOf: "isoWeek",
    xAxisLabelFormat: "[Week] W (D MMM)",
  },
  MONTHLY: { resolution: "MONTHLY", label: "Monthly", duration: "month", startOf: "month", xAxisLabelFormat: "MMM" },
  QUARTERLY: {
    resolution: "QUARTERLY",
    label: "Quarterly",
    duration: "quarter",
    startOf: "quarter",
    xAxisLabelFormat: "MMM",
  }, // TODO xaxis
};

export type MetricType =
  | { type: "RATE" | "COUNT" | "PERCENTAGE" | "DECIMAL" | "STRING" }
  | { type: "CURRENCY"; currencyCode: string };

export type Metric = {
  name: string;
  success_direction: "up" | "down";
} & MetricType;

export type RevenueDefinition = "Default" | "AmazonMetrics" | "GoogleAnalytics";

export type TableData = {
  data: string[][];
  partitionDimensions: DimensionValue[];
  groupByDimensions: string[];
  filterDimensions: DimensionValue[];
  filterMetrics: MetricValue[];
  timeRange: TimeRange;
  endDate: number;
  metrics: Metric[];
  currencyCode: string;
};

export type Point = {
  index: string;
  values: any;
};

export type Series = {
  values: Point[];
};

export type Axis = {
  label: string;
};

export type GraphData = {
  xAxis: Axis;
  series: Series[];
  endDate: number;
  timeRange: string;
  metrics: Metric[];
};

export type Legend = {
  title: string;
  disabled: boolean;
};

export type ChartData = ChartPoint[];

export type ChartPoint = {
  x: number;
  y: number;
  size: number;
};

export type TimeRangeItem = { text: string; id: string };

export type Tab = {
  title: string;
  metricType: MetricTypes;
  name?: string;
};
